import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PricingHeader from './components/PricingHeader';
import PricingCard from './components/PricingCard';
import TrustSignals from './components/TrustSignals';
import FAQ from './components/FAQ';

const PricingPlans = () => {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      id: 'launch',
      name: 'Launch',
      price: '29',
      billing: 'month',
      description: 'Perfect for individuals getting started with AI prompts',
      features: [
        'Up to 30 basic prompts per month',
        'General AI-powered suggestions',
        'Standard response time',
        'Email support',
        'Basic prompt categories',
        'Web-based access'
      ],
      buttonText: 'Get Started Free',
      buttonVariant: 'default'
    },
    {
      id: 'scale-up',
      name: 'Scale-Up',
      price: '69',
      billing: 'month',
      description: 'Ideal for professionals and growing teams',
      features: [
        'Unlimited categorized prompts',
        'Save and organize prompts',
        'Advanced AI classification',
        'Priority support',
        'Custom prompt templates',
        'Team collaboration tools',
        'Export functionality',
        'Analytics dashboard'
      ],
      buttonText: 'Choose Scale-Up',
      buttonVariant: 'default',
      isRecommended: true
    },
    {
      id: 'command',
      name: 'Command',
      price: '129',
      billing: 'month',
      description: 'Enterprise solution with advanced customization',
      features: [
        'Everything in Scale-Up',
        'Custom GPT system prompts',
        'Master workflow builder',
        'API access',
        'White-label options',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced security controls',
        'SLA guarantee'
      ],
      buttonText: 'Choose Command',
      buttonVariant: 'default'
    }
  ];

  const handleSelectPlan = (plan) => {
    if (plan?.id === 'launch') {
      navigate('/user-authentication', { 
        state: { 
          planId: plan?.id,
          action: 'signup',
          redirectTo: '/prompt-generator'
        }
      });
    } else {
      navigate('/user-authentication', { 
        state: { 
          planId: plan?.id,
          action: 'signup',
          redirectTo: '/prompt-generator'
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <PricingHeader />
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 px-6 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {pricingPlans?.map((plan) => (
                <PricingCard
                  key={plan?.id}
                  plan={plan}
                  isRecommended={plan?.isRecommended}
                  onSelectPlan={handleSelectPlan}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <TrustSignals />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-6 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <FAQ />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Transform Your Prompt Generation?
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Join thousands of professionals who are already using PitchEngine AI to create better prompts faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => handleSelectPlan(pricingPlans?.[0])}
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Start Free Trial
              </button>
              <button
                onClick={() => navigate('/user-authentication')}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors"
              >
                View Demo
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  <span className="text-xl font-semibold">PitchEngine AI</span>
                </div>
                <p className="text-gray-400 text-sm">
                  AI-powered prompt generation for modern professionals.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Product</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><a href="/pricing-plans" className="hover:text-white transition-colors">Pricing</a></li>
                  <li><a href="/prompt-generator" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Support</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                  <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
              <p>&copy; {new Date()?.getFullYear()} PitchEngine AI. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default PricingPlans;